import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import trainingApi from './api';
import { useTrainingLang } from './i18n/TrainingLangContext';
import { nativeLabelFor } from './i18n/strings';

const MyHistory = () => {
  const { language, t } = useTrainingLang();
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    (async () => {
      try {
        const data = await trainingApi.myHistory(language);
        if (mounted) { setAttempts(data); setError(''); }
      } catch (e) {
        if (mounted) setError(e.message);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [language]);

  // Group attempts by training for the per-training rollup (best score + last attempt
  // + status). Attempts in every language now roll up into one row per training, which
  // is the whole point of the merge — a Spanish pass and an English pass are the same
  // training, not two.
  const rollup = useMemo(() => {
    const map = new Map();
    for (const a of attempts) {
      const cur = map.get(a.training_id) || {
        training_id: a.training_id,
        title: a.title,
        attempts: 0,
        best_score: null,
        last_attempt: null,
        passed: false,
        languages: new Set(),
      };
      cur.attempts += 1;
      if (a.language) cur.languages.add(a.language);
      if (a.score_pct !== null && a.score_pct !== undefined) {
        const score = Number(a.score_pct);
        if (cur.best_score === null || score > cur.best_score) cur.best_score = score;
      }
      const stamp = new Date(a.completed_at || a.started_at);
      if (!cur.last_attempt || stamp > cur.last_attempt) cur.last_attempt = stamp;
      if (a.passed) cur.passed = true;
      map.set(a.training_id, cur);
    }
    return [...map.values()].sort((a, b) => (b.last_attempt || 0) - (a.last_attempt || 0));
  }, [attempts]);

  return (
    <div className="p-8 text-slate-100">
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-1">{t('historyTitle')}</h1>
        <p className="text-slate-400 text-sm">{t('historySubtitle')}</p>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded bg-rose-900/40 border border-rose-700 text-rose-200">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-slate-400">{t('loading')}</div>
      ) : rollup.length === 0 ? (
        <div className="text-slate-400">{t('historyEmpty')}</div>
      ) : (
        <div className="overflow-auto rounded-lg border border-slate-700">
          <table className="w-full text-sm">
            <thead className="bg-slate-800 text-slate-300">
              <tr>
                <th className="text-left px-4 py-3">{t('colTraining')}</th>
                <th className="text-left px-4 py-3">{t('colStatus')}</th>
                <th className="text-left px-4 py-3">{t('colAttempts')}</th>
                <th className="text-left px-4 py-3">{t('colBestScore')}</th>
                <th className="text-left px-4 py-3">{t('colLanguage')}</th>
                <th className="text-left px-4 py-3">{t('colLastAttempt')}</th>
                <th className="text-left px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rollup.map((r) => (
                <tr key={r.training_id} className="border-t border-slate-700">
                  <td className="px-4 py-3 font-medium">{r.title}</td>
                  <td className="px-4 py-3">
                    {r.passed ? (
                      <span className="text-emerald-300">{t('statusPassed')}</span>
                    ) : r.attempts > 0 ? (
                      <span className="text-amber-300">{t('statusInProgress')}</span>
                    ) : (
                      <span className="text-slate-400">{t('statusNotStarted')}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-300">{r.attempts}</td>
                  <td className="px-4 py-3 text-slate-300">
                    {r.best_score !== null ? `${r.best_score.toFixed(0)}%` : '—'}
                  </td>
                  <td className="px-4 py-3 text-slate-400">
                    {[...r.languages].map((code) => nativeLabelFor(code)).join(', ') || '—'}
                  </td>
                  <td className="px-4 py-3 text-slate-400">
                    {r.last_attempt ? r.last_attempt.toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/tiles/ksp_training/take/${r.training_id}`}
                      className="text-amber-400 hover:text-amber-300"
                    >
                      {r.passed ? t('actionReview') : t('actionContinue')}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MyHistory;
